import React, { useState } from 'react';
import Modal from '../common/Modal';
import { Button } from '../common/Button';
import api from '../../services/api';
import toast from 'react-hot-toast';
import { RfqPreviewModal } from './RfqPreviewModal';

interface Carrier {
  id: number;
  name: string;
  email?: string;
}

interface Package {
  length_mm?: number;
  width_mm?: number;
  height_mm?: number;
  weight_kg?: number;
}

interface SendRfqModalProps {
  isOpen: boolean;
  onClose: () => void;
  transportId: number;
  carriers: Carrier[];
  packages: Package[];
  productDescriptions: string[];
  onSuccess: () => void;
}

export const SendRfqModal = ({
  isOpen,
  onClose,
  transportId,
  carriers,
  packages,
  productDescriptions,
  onSuccess
}: SendRfqModalProps) => {
  const [sending, setSending] = useState(false);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [clientInfo, setClientInfo] = useState<any>(null);

  // Load client info
  React.useEffect(() => {
    if (isOpen && transportId) {
      loadClientInfo();
    }
  }, [isOpen, transportId]);

  const loadClientInfo = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`/api/v1/transport/${transportId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setClientInfo(data);
      }
    } catch (error) {
      console.error('Failed to load client info:', error);
    }
  };

  const handleSend = async () => {
    if (carriers.length === 0) {
      toast.error('Brak przewoźników do wysłania zapytania');
      return;
    }

    // Validate emails
    for (const carrier of carriers) {
      if (!carrier.email || !carrier.email.includes('@')) {
        toast.error(`Nieprawidłowy email dla przewoźnika: ${carrier.name}`);
        return;
      }
    }

    try {
      setSending(true);

      // Send RFQs to all carriers
      await api.post(`/transport/${transportId}/send-rfq`, {});

      toast.success(`Wysłano zapytania do ${carriers.length} przewoźników`);
      onSuccess();
      onClose();
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Nie udało się wysłać zapytań');
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} title="Wyślij zapytanie ofertowe (RFQ)" size="xl">
        <div className="space-y-6">

          {/* Dane klienta */}
          {clientInfo && (
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Dane klienta:</h3>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  {clientInfo.client_company && (
                    <div>
                      <span className="text-gray-600">Firma:</span>{' '}
                      <span className="font-medium">{clientInfo.client_company}</span>
                    </div>
                  )}
                  {(clientInfo.client_name || clientInfo.client_surname) && (
                    <div>
                      <span className="text-gray-600">Osoba kontaktowa:</span>{' '}
                      <span className="font-medium">{[clientInfo.client_name, clientInfo.client_surname].filter(Boolean).join(' ')}</span>
                    </div>
                  )}
                  {clientInfo.client_email && (
                    <div>
                      <span className="text-gray-600">Email:</span>{' '}
                      <span className="font-medium">{clientInfo.client_email}</span>
                    </div>
                  )}
                  {clientInfo.delivery_address && (
                    <div className="col-span-2">
                      <span className="text-gray-600">Adres dostawy:</span>{' '}
                      <span className="font-medium">{clientInfo.delivery_address}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Lista przewoźników */}
          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-3">
              Zapytanie zostanie wysłane do następujących przewoźników:
            </h3>
            <div className="space-y-2 max-h-48 overflow-y-auto border rounded-lg p-3 bg-gray-50">
              {carriers.length === 0 ? (
                <div className="text-sm text-gray-500">Brak przewoźników przypisanych do tego zlecenia</div>
              ) : (
                carriers.map(carrier => (
                  <div key={carrier.id} className="flex items-start gap-3 p-2 bg-white rounded border">
                    <div className="flex-1">
                      <div className="font-medium text-sm">{carrier.name}</div>
                      <div className="flex items-center gap-2 mt-1">
                        <label className="text-xs text-gray-500">Email:</label>
                        <span className="text-sm text-gray-700">{carrier.email || 'Brak adresu email'}</span>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Wymiary paczek */}
          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-2">Wymiar ładowny:</h3>
            <div className="bg-gray-50 border rounded-lg p-3 space-y-2">
              {packages.length === 0 ? (
                <div className="text-sm text-gray-500">Brak danych o wymiarach</div>
              ) : (
                packages.map((pkg, idx) => (
                  <div key={idx} className="text-sm">
                    <span className="font-medium">Paczka {idx + 1}:</span>{' '}
                    {pkg.length_mm || 0}mm × {pkg.width_mm || 0}mm × {pkg.height_mm || 0}mm, {pkg.weight_kg || 0}kg
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Produkty */}
          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-2">Produkty w zamówieniu:</h3>
            <div className="bg-gray-50 border rounded-lg p-3 space-y-1">
              {productDescriptions.length === 0 ? (
                <div className="text-sm text-gray-500">Brak opisów produktów</div>
              ) : (
                productDescriptions.map((desc, idx) => (
                  <div key={idx} className="text-sm">• {desc || 'Brak opisu'}</div>
                ))
              )}
            </div>
          </div>

          {/* Przyciski */}
          <div className="flex justify-between items-center pt-4 border-t">
            <Button
              variant="outline"
              onClick={() => setPreviewModalOpen(true)}
              className="bg-blue-50 hover:bg-blue-100 text-blue-700 border-blue-300"
            >
              Podgląd wiadomości
            </Button>

            <div className="flex gap-2">
              <Button variant="outline" onClick={onClose} disabled={sending}>
                Anuluj
              </Button>
              <Button
                onClick={handleSend}
                disabled={sending || carriers.length === 0}
                className="bg-green-600 hover:bg-green-700 text-white"
              >
                {sending ? 'Wysyłanie...' : `Wyślij zapytania do ${carriers.length} przewoźników`}
              </Button>
            </div>
          </div>
        </div>
      </Modal>

      <RfqPreviewModal
        isOpen={previewModalOpen}
        onClose={() => setPreviewModalOpen(false)}
        transportId={transportId}
      />
    </>
  );
};
